import * as sdkTypes from '@vansite/ts-sharetribe-flex-sdk/types';

import { FacebookAddToWishlistEvent } from '../analytics/facebook';
import { createListingItem } from '../analytics/conversions';
import { FirebaseAddToWishlistEvent } from '../analytics/firebase';
import { storableError } from '../util/errors';
import { logError } from '../util/log';
import { getListingsById } from './marketplaceData.duck';
import { fetchCurrentUser } from './user.duck';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const CURRENT_BOOKMARKS = 'app/bookmarks/CURRENT_BOOKMARKS';

export const ADD_BOOKMARK_REQUEST = 'app/bookmarks/ADD_BOOKMARK_REQUEST';
export const ADD_BOOKMARK_REQUEST_SUCCESS = 'app/bookmarks/ADD_BOOKMARK_REQUEST_SUCCESS';
export const ADD_BOOKMARK_REQUEST_ERROR = 'app/bookmarks/ADD_BOOKMARK_REQUEST_ERROR';

export const REMOVE_BOOKMARK_REQUEST = 'app/bookmarks/REMOVE_BOOKMARK_REQUEST';
export const REMOVE_BOOKMARK_SUCCESS = 'app/bookmarks/REMOVE_BOOKMARK_SUCCESS';
export const REMOVE_BOOKMARK_ERROR = 'app/bookmarks/REMOVE_BOOKMARK_ERROR';

export const OPEN_BOOKMARK_LOGIN_REQUIRED_MODAL_REQUEST =
  'app/bookmarks/OPEN_BOOKMARK_LOGIN_REQUIRED_MODAL_REQUEST';
export const CLOSE_BOOKMARK_LOGIN_REQUIRED_MODAL_REQUEST =
  'app/bookmarks/CLOSE_BOOKMARK_LOGIN_REQUIRED_MODAL_REQUEST';

// ================ Reducer ================ //

/** @type {import('../../types/vansite').VanSite.State.Bookmarks} */
const initialState = {
  addBookmarkInProgress: false,
  addBookmarkError: null,
  removeBookmarkInProgress: false,
  removeBookmarkError: null,
  currentBookmarks: [],
  bookmarkedListings: null,
  bookmarkLoginRequiredModalOpen: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CURRENT_BOOKMARKS:
      return { ...state, currentBookmarks: payload };
    case ADD_BOOKMARK_REQUEST:
      return { ...state, addBookmarkError: null, addBookmarkInProgress: true };
    case ADD_BOOKMARK_REQUEST_SUCCESS:
      return { ...state, currentBookmarks: payload, addBookmarkInProgress: false };
    case ADD_BOOKMARK_REQUEST_ERROR:
      return { ...state, addBookmarkError: payload, addBookmarkInProgress: false };

    case REMOVE_BOOKMARK_REQUEST:
      return { ...state, removeBookmarkError: null, removeBookmarkInProgress: true };
    case REMOVE_BOOKMARK_SUCCESS:
      return { ...state, currentBookmarks: payload, removeBookmarkInProgress: false };
    case REMOVE_BOOKMARK_ERROR:
      return { ...state, addBookmarkError: payload, removeBookmarkInProgress: false };

    case OPEN_BOOKMARK_LOGIN_REQUIRED_MODAL_REQUEST:
      return { ...state, bookmarkLoginRequiredModalOpen: true };
    case CLOSE_BOOKMARK_LOGIN_REQUIRED_MODAL_REQUEST:
      return { ...state, bookmarkLoginRequiredModalOpen: false };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fillCurrentBookmark = (bookmarks) => ({
  type: CURRENT_BOOKMARKS,
  payload: bookmarks,
});

export const bookmarksAddRequest = () => ({ type: ADD_BOOKMARK_REQUEST });

export const bookmarksAddSuccess = (bookmarks) => ({
  type: ADD_BOOKMARK_REQUEST_SUCCESS,
  payload: bookmarks,
});

export const bookmarksAddError = (error) => ({
  type: ADD_BOOKMARK_REQUEST_ERROR,
  payload: error,
  error: true,
});

export const bookmarksRemoveRequest = () => ({ type: REMOVE_BOOKMARK_REQUEST });

export const bookmarksRemoveSuccess = (bookmarks) => ({
  type: REMOVE_BOOKMARK_SUCCESS,
  payload: bookmarks,
});

export const bookmarksRemoveError = (error) => ({
  type: REMOVE_BOOKMARK_ERROR,
  payload: error,
  error: true,
});

export const openBookmarkLoginRequiredModalRequest = () => ({
  type: OPEN_BOOKMARK_LOGIN_REQUIRED_MODAL_REQUEST,
});
export const closeBookmarkLoginRequiredModalRequest = () => ({
  type: CLOSE_BOOKMARK_LOGIN_REQUIRED_MODAL_REQUEST,
});

// ================ Selectors ================ //

const bookmarkEventTracking = (listing) => {
  FirebaseAddToWishlistEvent.withValue(listing.attributes.price.amount / 100)
    .withCurrency(listing.attributes.price.currency.toLowerCase())
    .withItems(createListingItem(listing))
    .logEvent();
  FacebookAddToWishlistEvent.withValue(listing.attributes.price.amount / 100)
    .withCurrency(listing.attributes.price.currency.toLowerCase())
    .withContentCategory(listing.attributes.publicData.category)
    .withContentIds([listing.id.uuid])
    .withContentName(listing.attributes.title)
    .withContents([{ id: listing.id.uuid, quantity: 1, item_price: listing.attributes.price.amount / 100 }])
    .logEvent();
};

// ================ Thunks ================ //

export const addBookmark = (listingId) => async (dispatch, getState, sdk) => {
  const { isAuthenticated } = getState().auth;
  if (!isAuthenticated) {
    dispatch(openBookmarkLoginRequiredModalRequest());
    return;
  }

  dispatch(bookmarksAddRequest());
  const { currentBookmarks } = getState().bookmarks;
  const netBookmarks = [...currentBookmarks];

  if (
    currentBookmarks.length === 0 ||
    (currentBookmarks && !currentBookmarks.includes(listingId) && isAuthenticated)
  ) {
    netBookmarks.push(listingId);
  } else {
    return currentBookmarks;
  }

  try {
    const response = await sdk.currentUser.updateProfile(
      {
        privateData: {
          bookmarks: netBookmarks,
        },
      },
      {
        expand: true,
        include: ['profileImage'],
      },
    );
    const { bookmarks } = response.data.data.attributes.profile.privateData;
    dispatch(bookmarksAddSuccess(bookmarks));
    dispatch(fetchCurrentUser());
    const listing = getListingsById(getState().marketplaceData.entities, [new UUID(listingId)])[0];
    bookmarkEventTracking(listing);
    return bookmarks;
  } catch (e) {
    logError(e, 'add-bookmark-failed');
    dispatch(bookmarksAddError(storableError(e)));
  }
};

export const removeBookmark = (listingId) => async (dispatch, getState, sdk) => {
  const { isAuthenticated } = getState().auth;
  if (!isAuthenticated) {
    dispatch(openBookmarkLoginRequiredModalRequest());
    return;
  }

  dispatch(bookmarksRemoveRequest());
  const { currentBookmarks } = getState().bookmarks;

  const index = currentBookmarks.indexOf(listingId);
  if (index !== -1) {
    currentBookmarks.splice(index, 1);
  }

  try {
    const response = await sdk.currentUser.updateProfile(
      {
        privateData: {
          bookmarks: currentBookmarks,
        },
      },
      {
        expand: true,
        include: ['profileImage'],
      },
    );
    const { bookmarks } = response.data.data.attributes.profile.privateData;
    dispatch(bookmarksRemoveSuccess(bookmarks));
    dispatch(fetchCurrentUser());
    return bookmarks;
  } catch (e) {
    logError(e, 'remove-bookmark-failed');
    dispatch(bookmarksRemoveError(storableError(e)));
  }
};

export const openBookmarkLoginRequiredModal = () => (dispatch) => {
  dispatch(openBookmarkLoginRequiredModalRequest());
};

export const closeBookmarkLoginRequiredModal = () => (dispatch) => {
  dispatch(closeBookmarkLoginRequiredModalRequest());
};
